/**
 * AdFraudTracker — invisible ad click-fraud recorder.
 *
 * When a visitor arrives with a `gclid` in the URL (i.e. clicked a Google
 * Ad and cost you money), we log the session + IP + UA server-side.
 * If the visitor then scrolls or clicks anything, we record engagement.
 * Sessions that never engage become "abusive IPs" in the report card
 * exposed at GET /api/ads/fraud/report — paste those IPs into Google
 * Ads → Excluded IPs to stop the waste.
 *
 * Mount once at the app root. No UI. Safe to render on every page.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SESSION_KEY = "cr_ad_fraud_session";

function ensureSessionId() {
  try {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, s);
    }
    return s;
  } catch {
    return `s_${Date.now().toString(36)}`;
  }
}

export default function AdFraudTracker() {
  const loc = useLocation();
  const engagedRef = useRef(false);

  // Detect + log a paid landing.
  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const gclid = params.get("gclid");
    if (!gclid) return;

    const session_id = ensureSessionId();
    const payload = {
      session_id,
      gclid,
      utm_source: params.get("utm_source"),
      utm_medium: params.get("utm_medium"),
      utm_campaign: params.get("utm_campaign"),
      landing_url: window.location.href,
    };
    fetch(`${API}/api/ads/fraud/click`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => { /* best-effort */ });
  }, [loc.search]);

  // Record engagement — first scroll OR first genuine click OR form focus.
  useEffect(() => {
    function fireEngagement(kind) {
      if (engagedRef.current) return;
      engagedRef.current = true;
      const session_id = ensureSessionId();
      fetch(`${API}/api/ads/fraud/engagement`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id, kind }),
        keepalive: true,
      }).catch(() => {});
    }
    const onScroll = () => { if (window.scrollY > 60) fireEngagement("scroll"); };
    const onClick = (e) => {
      // Ignore auto-fired synthetic clicks that some bots send.
      if (e && e.isTrusted !== false) fireEngagement("click");
    };
    const onFocus = () => fireEngagement("form");
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("click", onClick);
    window.addEventListener("focusin", onFocus);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("click", onClick);
      window.removeEventListener("focusin", onFocus);
    };
  }, []);

  return null;
}
